/**
 * The address of a screen's table narrowed to what a KPI card counts (ADR 9069).
 *
 * A card that counts «the rows of this table where column X is V» opens the same page with that filter on
 * the table: `?<urlKey>.f.<column>=v:<value>` — the table's own filter, written by the table's own writer
 * (`writeTableUrlView`), so the link and a hand-made filter are the same address. The page's other
 * parameters (the market, a tab) stay; the table's previous sort and filters do not, because they are not
 * in the card and a card opens exactly the rows it counts.
 *
 * A pure function of the page's search string, so the caller recomputes it as the address changes. Shared by every chapter's
 * cards: one mechanism, not one per screen. Ranked tables (`useRankedCap`) have the same builder in `rankedViewHref`.
 */

import { carriedSearch } from './carried-search';
import { writeTableUrlView } from './data-table';

/** One column's filter: the values it keeps (`v:`), or a numeric range (`r:`, a bound left empty is open). */
export type ColumnFilterSpec = readonly string[] | { readonly from: string; readonly to: string };

/** What a link may add to the filters: the element to scroll to, and which of the page's parameters travel. */
export interface TableFilterLinkOptions {
  /** The id of the element to scroll to once the page is open (the card and its table share a page, or the table is below the fold). */
  readonly fragment?: string;
  /**
   * The page's parameters the destination reads too and the card counted under — ONLY these travel (a blank one does not).
   * Left out, every parameter of the page stays. Stated by the caller, so a page is never made to carry a parameter it
   * does not read (the URL-parameter census).
   */
  readonly carry?: readonly string[];
}

/**
 * `path` with the table `urlKey` narrowed to the rows whose columns match `filters` (the column's `value()` strings, or a
 * range). An empty `filters` is the table unfiltered, its previous sort and filters dropped.
 */
export function tableFilterHref(
  path: string,
  search: string,
  urlKey: string,
  filters: Readonly<Record<string, ColumnFilterSpec>>,
  options: TableFilterLinkOptions = {},
): string {
  const base = options.carry === undefined ? search : carriedSearch(search, options.carry);
  const query = writeTableUrlView(base, urlKey, {
    sort: null,
    filters: Object.fromEntries(Object.entries(filters).map(([column, spec]) => [
      column,
      'from' in spec ? { kind: 'range' as const, from: spec.from, to: spec.to } : { kind: 'values' as const, values: spec },
    ])),
  });
  return `${path}${query}${options.fragment === undefined ? '' : `#${options.fragment}`}`;
}
